import { memo, useState, useEffect, useCallback, useMemo } from "react";
import Form from "../../components/Form";
import {
    getOwners,
    getLambWeights,
    addLambWeight,
    updateLambWeight,
    getUserRole,
    deleteLambWeight,
} from "../../api";
import { useSnackbar } from "../../utils/index.js";
import useSelectStyles from "../../components/styles/Select";
import { Box, Typography, Grid2, Button, Modal, Slide, IconButton } from "@mui/material";
import Spinner from "../../components/Spinner.js";
import useListStyles from "../../components/styles/List.js";
import Table from "../../components/Table.js";
import {
    weightDateHeader,
    weightHeader,
} from "../../components/TableHeaders.js"
import AddIcon from '@mui/icons-material/Add';
import AreYouSurePopup from "../../components/popups/AreYouSurePopup.js";
import DeleteIcon from "@mui/icons-material/Delete";
import {dayjs} from "../../utils/index.js";

const LambWeight = () => {
    const [fetchedOwners, setFetchedOwners] = useState([]);
    const [ownersDropdownValues, setOwnersDropdownValues] = useState([]);
    const [selectedOwnerId, setSelectedOwnerId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [step, setStep] = useState(0);
    const [fetchedWeights, setFetchedWeights] = useState([]);
    const [selectedLambSlug, setSelectedLambSlug] = useState(null);
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [updateWeight, setUpdateWeight] = useState(null);
    const [isUpdateOpen, setIsUpdateOpen] = useState(false);
    const [userRole, setUserRole] = useState(null);
    const [isPopupOpen, setIsPopupOpen] = useState(false);

    const classes = useSelectStyles();
    const listClasses = useListStyles();
    const { success, error } = useSnackbar();

    const fetchData = useCallback(
        async () => {
            setIsLoading(true);
            try {
                const { owners } = await getOwners();
                setFetchedOwners(owners);
                setOwnersDropdownValues(owners.map((owner) => ({
                    value: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`,
                    text: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`
                })));
                const { role } = await getUserRole();
                setUserRole(role);
            } catch (err) {
                error(err);
            }
            setIsLoading(false);
        }
    )

    useEffect(() => {
        fetchData();
    }, []);

    const handelWeightDateHeaderClick = (value) => {
        const weightToUpdate = fetchedWeights.find((weight) => weight.date === value);
        setUpdateWeight(weightToUpdate);
        setIsUpdateOpen(true);
    }

    const tableColumns = useMemo(() => [
        weightDateHeader(handelWeightDateHeaderClick),
        weightHeader,
    ]);

    const ownerFormContent = [
        {
            customType: "dropdown",
            id: "owner",
            items: ownersDropdownValues,
            placeholder: "Εκτροφή",
            required: true,
        },
        {
            customType: "input",
            id: "lambSlug",
            type: "text",
            placeholder: "Κωδικός Αμνού",
            required: true,
            helperText: "4-ψήφιος κωδικός Αμνού"
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Επόμενο"
        },
    ]

    const weightForm = [
        {
            customType: "date-picker",
            id: "weightDate",
            type: "desktop",
            label: "Ημ/νία Ζύγισης",
            width: "350px",
            background: "third",
            required: true,
            value: dayjs(updateWeight?.date) || null,
            disabled: isUpdateOpen ? true : false,
        },
        {
            customType: "input",
            id: "weight",
            type: "number",
            placeholder: "Βάρος Αμνού",
            required: true,
            value: updateWeight?.weight || null,
            disabled: (isUpdateOpen && userRole !== "ADMIN") ? true : false,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: isCreateOpen ? "Προσθήκη" : "Ενημέρωση",
            disabled: (isUpdateOpen && userRole !== "ADMIN") ? true : false,
        },
    ]

    const fetchWeights = async (lambSlug, ownerId) => {
        setIsLoading(true);
        const { success: scs, weights, message } = await getLambWeights(lambSlug, ownerId);
        if (scs) {
            setFetchedWeights(weights);
            setStep(1);
        } else {
            error(message);
        }
        setIsLoading(false);
    }

    const handleOwnerSelect = async (values) => {
        const ownerId = fetchedOwners.find((owner) => `${owner?.lastName} ${owner?.firstName} - ${owner?.code}` === values.owner)?._id;
        setSelectedOwnerId(ownerId);
        setSelectedLambSlug(values.lambSlug);
        fetchWeights(values.lambSlug, ownerId);
    }

    const handleWeightAddSubmit = async (values) => {
        setIsLoading(true);
        const weightObj = {
            date: values.weightDate,
            weight: values.weight,
        };
        const { success: scs, message } = await addLambWeight(selectedLambSlug, selectedOwnerId, weightObj)
        if (scs) {
            success(message);
            setIsCreateOpen(false);
            fetchWeights(selectedLambSlug, selectedOwnerId);
        } else {
            error(message);
            setIsCreateOpen(false);
        }
        setIsLoading(false);
    };

    const handleWeightUpdateSubmit = async (values) => {
        setIsLoading(true);
        const bodyObj = {
            date: updateWeight.date,
            weight: values.weight,
        };

        const { success: scs, message } = await updateLambWeight(selectedLambSlug, selectedOwnerId, bodyObj);
        if (scs) {
            success(message);
        } else {
            error(message);
        }
        fetchWeights(selectedLambSlug, selectedOwnerId);
        setUpdateWeight(null);
        setIsUpdateOpen(false);
        setIsLoading(false);
    };

    const handleLambWeightDelete = async () => {
        setIsLoading(true);
        const values = {
            date: updateWeight.date,
        }
        const { success: scs, message } = await deleteLambWeight(selectedLambSlug, selectedOwnerId, values);
        if (scs) {
            success(message);
        } else {
            error(message);
        }
        fetchWeights(selectedLambSlug, selectedOwnerId);
        setUpdateWeight(null);
        setIsPopupOpen(false);
        setIsUpdateOpen(false);
        setIsLoading(false);
    }
    return (
        <>
            <Spinner open={isLoading} />
            {step === 0 && (
                <Box className={classes.outerBox}>
                    <Box container flexDirection="column" className={classes.innerBox}>
                        <Typography variant="h5" sx={{ margin: "1rem" }}>
                            Επιλέξτε Εκτροφή και Αμνό
                        </Typography>
                        <Form content={ownerFormContent} onSubmit={handleOwnerSelect} toResetForm={false} />
                    </Box>
                </Box>
            )}
            {step === 1 && (
                <>
                    <AreYouSurePopup
                        width={"900px"}
                        isOpen={isPopupOpen}
                        title="Διαγραφή Ζύγισης"
                        titleColor="black"
                        onClose={() => setIsPopupOpen(false)}
                        innerText={`Είστε σίγουροι ότι θέλετε να διαγράψετε τη Ζύγιση που έγινε στις ${new Date(updateWeight?.date).toLocaleDateString("el-GR")};`}
                        declineText="Πίσω"
                        onDecline={() => setIsPopupOpen(false)}
                        acceptText="Ναι, Διαγραφή Ζύγισης"
                        onAccept={() => handleLambWeightDelete()}
                    />
                    <Modal open={isCreateOpen} onClose={() => setIsCreateOpen(false)} sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <Slide direction="left" in={isCreateOpen} mountOnEnter unmountOnExit>
                            <Box className={listClasses.modalContent}>
                                <Typography variant="h5" className={listClasses.modal_title}>
                                    Προσθήκη Ζύγισης
                                </Typography>
                                <Form content={weightForm} onSubmit={handleWeightAddSubmit} />
                            </Box>
                        </Slide>
                    </Modal>
                    <Modal
                        open={isUpdateOpen}
                        onClose={() => {
                            setIsUpdateOpen(false);
                            setUpdateWeight(null);
                        }}
                        sx={{ display: "flex", justifyContent: "flex-end" }}
                    >
                        <Slide direction="left" in={isUpdateOpen} mountOnEnter unmountOnExit>
                            <Box className={listClasses.modalContent}>
                                <Typography variant="h5" className={listClasses.modal_title}>
                                    Επεξεργασία Ζύγισης
                                </Typography>
                                <Form content={weightForm} onSubmit={handleWeightUpdateSubmit} />
                                {userRole === "ADMIN" && isUpdateOpen && (
                                    <Box sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}>
                                        <IconButton
                                            aria-label="delete"
                                            onClick={() => setIsPopupOpen(true)}
                                        >
                                            <DeleteIcon />
                                        </IconButton >
                                    </Box>
                                )}
                            </Box>
                        </Slide>
                    </Modal>
                    <Grid2 container className={listClasses.container} direction="column" sx={{ marginTop: "1rem", marginLeft: "3rem" }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ width: "90%", marginBottom: "1rem" }}>
                            <Typography variant="h6" className={listClasses.form_title}>
                                {`Βάρος Αμνού "${selectedLambSlug}"`}
                            </Typography>
                            <Box>
                                <Button variant="contained" sx={{ backgroundColor: "secondary.main" }} onClick={() => setIsCreateOpen(true)}>
                                    <AddIcon sx={{ mr: "0.5rem" }} />Προσθήκη Ζύγισης
                                </Button>
                            </Box>
                        </Box>
                        <Table
                            data={fetchedWeights}
                            noDataText={isLoading ? "Φόρτωση..." : "Δεν υπάρχουν εγγραφές!"}
                            columns={tableColumns}
                            defaultSorted={[{ id: "date", desc: true }]}
                        />
                    </Grid2>
                </>
            )}
        </>
    )
}

export default memo(LambWeight)

